import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { useOnError } from 'src/utils/errors';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ServicePlan } from 'src/features/servicePlans/utils/types';
import { SERVICE_PLAN_RESOURCE } from 'src/features/servicePlans/api/useGetServicePlan';
import { LIST_SERVICE_PLANS_RESOURCE } from 'src/features/servicePlans/api/useListServicePlans';
import { servicePlansMessages } from 'src/features/servicePlans/utils/messages';
import { useActivateServicePlan } from 'src/features/servicePlans/api/useActivateServicePlan';

interface ActivateServicePlanFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedServicePlan: ServicePlan;
    children?: React.ReactNode;
}

function ActivateServicePlanFormModal<TData>({
    selectedServicePlan,
    children,
}: ActivateServicePlanFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const onError = useOnError();

    const form = useForm({
        resolver: zodResolver(z.object({})),
    });

    const { mutate: activateServicePlan } = useActivateServicePlan({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [SERVICE_PLAN_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [LIST_SERVICE_PLANS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(servicePlansMessages.activateServicePlanSuccess),
                description: formatMessage(servicePlansMessages.activateServicePlanSuccessDescription),
            });
        },
        onError: (error) => onError(error, form.setError),
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...servicePlansMessages.activateServicePlan },
                subTitle: {
                    ...servicePlansMessages.activateServicePlanSubtitle,
                    values: {
                        name: selectedServicePlan.name,
                    },
                },
                icon: ICON_MAP.TOGGLE_ON,
                confirmationMessage: { ...servicePlansMessages.confirmActivateServicePlan },
            }}
            callbackButton={{
                variant: 'default',
                buttonTitle: servicePlansMessages.activate,
                callbackFn: () => {
                    return (
                        activateServicePlan({
                            planId: selectedServicePlan.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default ActivateServicePlanFormModal;
